import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Typography,
  InputAdornment,
} from '@material-ui/core';
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import { makeStyles } from '@material-ui/core/styles';
import { useCart } from "../Context/CartContext";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    padding: theme.spacing(0.5, 0, 1),
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: "1px solid #f1f5f9",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  backButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#475569",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "#f1f5f9",
      color: "#0f172a",
      borderColor: "#cbd5e1",
    },
    "& .MuiSvgIcon-root": {
      fontSize: 15,
      marginLeft: 4,
    },
  },
  headerTitle: {
    fontSize: "1.15rem",
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  headerSubtitle: {
    fontSize: "0.78rem",
    color: "#64748b",
  },
  totalPill: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    backgroundColor: "#eff6ff",
    border: "1px solid #dbeafe",
    borderRadius: 10,
    padding: "4px 12px",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  totalLabel: {
    fontSize: "0.65rem",
    fontWeight: 700,
    color: "#2563eb",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  totalAmount: {
    fontSize: "1.1rem",
    fontWeight: 800,
    color: "#1d4ed8",
    fontVariantNumeric: "tabular-nums",
  },
  // Visual Virtual Card Mockup
  virtualCard: {
    width: "100%",
    maxWidth: 360,
    height: 146,
    margin: "8px auto 20px",
    borderRadius: 16,
    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.22), inset 0 1px 1px rgba(255, 255, 255, 0.2)",
    padding: "16px 20px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    color: "#ffffff",
    boxSizing: "border-box",
    border: "1px solid rgba(255, 255, 255, 0.12)",
  },
  cardTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  chipAndWifi: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  cardChip: {
    width: 34,
    height: 25,
    borderRadius: 4,
    background: "linear-gradient(135deg, #fde047 0%, #ca8a04 100%)",
    border: "1px solid #a16207",
    boxShadow: "inset 0 1px 2px rgba(255, 255, 255, 0.5)",
    position: "relative",
  },
  cardNumberDisplay: {
    fontSize: "1.2rem",
    fontWeight: 700,
    letterSpacing: "3px",
    color: "#f8fafc",
    fontVariantNumeric: "tabular-nums",
    textAlign: "center",
    textShadow: "0 1px 3px rgba(0,0,0,0.5)",
  },
  cardBottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardModeLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#94a3b8",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  cardReadyLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#38bdf8",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  fieldsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.75),
    marginBottom: theme.spacing(1.75),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.25),
    },
  },
  formControl: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#f8fafc",
      "&:hover": {
        backgroundColor: "#ffffff",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        borderColor: "#f97316",
      },
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.9rem",
      fontWeight: 600,
      color: "#64748b",
    },
  },
  cardNumberField: {
    width: "100%",
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#f8fafc",
      "&:hover": {
        backgroundColor: "#ffffff",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        borderColor: "#f97316",
      },
      "& input": {
        fontSize: "1rem",
        fontWeight: 600,
        letterSpacing: "0.02em",
      },
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.9rem",
      fontWeight: 600,
      color: "#64748b",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.74rem",
      color: "#64748b",
      marginTop: 4,
    },
  },
}));

const CardDialog = ({ setOpenCardDialog, invoiceData, handleCardPayment, currency }) => {
  const classes = useStyles();
  const { getTotalPrice } = useCart();

  const handleCardChange = (event) => {
    const { name, value } = event.target;
    if (event.target.value.length === 5 && name === 'cardNumber') {
      return;
    }
    const updatedCard = { ...invoiceData.card, [name]: value };
    handleCardPayment(updatedCard);
  };

  const cardType = invoiceData?.card?.cardType || "Visa";
  const cardMode = invoiceData?.card?.cardMode || "Debit";
  const last4 = invoiceData?.card?.cardNumber || "";
  const formattedDigits = last4 ? last4.slice(-4) : "••••";

  const renderBrandBadge = () => {
    if (cardType === "Visa") {
      return (
        <span style={{ fontSize: "1.2rem", fontWeight: 900, fontStyle: "italic", color: "#ffffff", letterSpacing: "0.06em" }}>
          VISA
        </span>
      );
    }
    if (cardType === "Mastercard") {
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: 18, height: 18, borderRadius: "50%", backgroundColor: "#ef4444", opacity: 0.9 }} />
          <div style={{ width: 18, height: 18, borderRadius: "50%", backgroundColor: "#f59e0b", opacity: 0.9, marginLeft: -7 }} />
        </div>
      );
    }
    if (cardType === "Rupay") {
      return (
        <span style={{ fontSize: "1.05rem", fontWeight: 900, color: "#ffffff", letterSpacing: "0.04em" }}>
          RuPay<span style={{ color: "#38bdf8", fontSize: "0.85rem" }}>▶</span>
        </span>
      );
    }
    return <CreditCardIcon style={{ color: "#94a3b8", fontSize: 24 }} />;
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerRow}>
        <div className={classes.headerLeft}>
          <IconButton
            onClick={() => setOpenCardDialog(false)}
            className={classes.backButton}
            title="Back to payment options"
          >
            <ArrowBackIosIcon />
          </IconButton>
          <div>
            <Typography className={classes.headerTitle}>Card Payment</Typography>
            <Typography className={classes.headerSubtitle}>
              Record transaction details for customer's card
            </Typography>
          </div>
        </div>

        <div className={classes.totalPill}>
          <span className={classes.totalLabel}>Total Due</span>
          <span className={classes.totalAmount}>
            {currency}{getTotalPrice ? getTotalPrice().toFixed(2) : "0.00"}
          </span>
        </div>
      </div>

      {/* Modern Virtual Card Mockup */}
      <div className={classes.virtualCard}>
        <div className={classes.cardTopRow}>
          <div className={classes.chipAndWifi}>
            <div className={classes.cardChip} />
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2.5" strokeLinecap="round">
              <path d="M7 16a6 6 0 0 1 0-8" />
              <path d="M10 19a10 10 0 0 1 0-14" />
              <path d="M13 22a14 14 0 0 1 0-20" />
            </svg>
          </div>
          <div>{renderBrandBadge()}</div>
        </div>

        <div className={classes.cardNumberDisplay}>
          ••••  ••••  ••••  {formattedDigits}
        </div>

        <div className={classes.cardBottomRow}>
          <span className={classes.cardModeLabel}>
            {cardMode.toUpperCase()} CARD
          </span>
          <span className={classes.cardReadyLabel}>
            TERMINAL READY
          </span>
        </div>
      </div>

      <div className={classes.fieldsGrid}>
        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel id="card-mode-label">Card Mode</InputLabel>
          <Select
            labelId="card-mode-label"
            id="card-mode"
            value={invoiceData?.card?.cardMode || ""}
            label="Card Mode"
            name="cardMode"
            onChange={handleCardChange}
          >
            <MenuItem value="Debit">Debit Card</MenuItem>
            <MenuItem value="Credit">Credit Card</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel id="card-type-label">Card Type</InputLabel>
          <Select
            labelId="card-type-label"
            id="card-type"
            value={invoiceData?.card?.cardType || ""}
            label="Card Type"
            name="cardType"
            onChange={handleCardChange}
          >
            <MenuItem value="Visa">Visa</MenuItem>
            <MenuItem value="Rupay">RuPay</MenuItem>
            <MenuItem value="Mastercard">Mastercard</MenuItem>
          </Select>
        </FormControl>
      </div>

      <TextField
        id="card-number"
        label="Card Number (Last 4 Digits)"
        variant="outlined"
        type="number"
        fullWidth
        name="cardNumber"
        value={invoiceData?.card?.cardNumber || ""}
        onChange={handleCardChange}
        className={classes.cardNumberField}
        placeholder="e.g. 4242"
        helperText="Enter the last 4 digits printed on customer's card (optional)"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CreditCardIcon style={{ color: "#94a3b8", fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};

export default CardDialog;
